
export interface CookieCategory {
  id: string;
  name: string;
  description: string;
  essential: boolean;
  cookies: CookieInfo[];
}

export interface CookieInfo {
  name: string;
  purpose: string;
  duration: string;
  provider: string;
  category: string;
}

export interface ConsentData {
  version: string;
  timestamp: number;
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  expiryDate: number;
}

class CookieManager {
  private consentKey = 'gdpr_consent';
  private currentVersion = '2.0';
  private maxAge = 13 * 30 * 24 * 60 * 60 * 1000; // 13 months in milliseconds

  private cookieCategories: CookieCategory[] = [
    {
      id: 'essential',
      name: 'Notwendige Cookies',
      description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
      essential: true,
      cookies: [
        {
          name: 'gdpr_consent',
          purpose: 'Speichert Ihre Cookie-Einstellungen',
          duration: '13 Monate',
          provider: 'AMMING GmbH',
          category: 'essential'
        }
      ]
    },
    {
      id: 'analytics',
      name: 'Analyse Cookies',
      description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.',
      essential: false,
      cookies: [
        {
          name: '_ga, _ga_*',
          purpose: 'Google Analytics - Besucherstatistiken',
          duration: '2 Jahre',
          provider: 'Google Ireland Limited',
          category: 'analytics'
        }
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen.',
      essential: false,
      cookies: [
        {
          name: '_fbp, _fbc',
          purpose: 'Facebook Pixel - Conversion Tracking',
          duration: '90 Tage',
          provider: 'Meta Platforms Ireland Limited',
          category: 'marketing'
        }
      ]
    },
    {
      id: 'preferences',
      name: 'Präferenz Cookies',
      description: 'Diese Cookies speichern Ihre Einstellungen und Präferenzen.',
      essential: false,
      cookies: [
        {
          name: 'theme_preference',
          purpose: 'Speichert Ihre Theme-Einstellung',
          duration: '1 Jahr',
          provider: 'AMMING GmbH',
          category: 'preferences'
        }
      ]
    }
  ];

  getCategories(): CookieCategory[] {
    return this.cookieCategories;
  }

  hasValidConsent(): boolean {
    const consent = this.getConsent();
    if (!consent) return false;
    
    // Check if consent is expired
    if (Date.now() > consent.expiryDate) {
      this.clearConsent();
      return false;
    }
    
    // Check if version is current
    if (consent.version !== this.currentVersion) {
      return false;
    }
    
    return true;
  }

  getConsent(): ConsentData | null {
    try {
      const stored = localStorage.getItem(this.consentKey);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  setConsent(preferences: Omit<ConsentData, 'version' | 'timestamp' | 'expiryDate'>): void {
    const consent: ConsentData = {
      ...preferences,
      version: this.currentVersion,
      timestamp: Date.now(),
      expiryDate: Date.now() + this.maxAge
    };
    
    localStorage.setItem(this.consentKey, JSON.stringify(consent));
    this.applyCookieSettings(consent);
    
    // Log consent for audit purposes
    console.log('GDPR Consent Updated:', {
      timestamp: new Date(consent.timestamp).toISOString(),
      preferences: preferences,
      version: consent.version
    });
  }

  clearConsent(): void {
    localStorage.removeItem(this.consentKey);
    this.cleanupCookies();
  }

  private applyCookieSettings(consent: ConsentData): void {
    // Load/unload scripts based on consent
    if (consent.analytics) {
      this.loadAnalyticsScripts();
    } else {
      this.removeAnalyticsCookies();
    }
    
    if (consent.marketing) {
      this.loadMarketingScripts();
    } else {
      this.removeMarketingCookies();
    }
  }

  private loadAnalyticsScripts(): void {
    // Google Analytics
    if (!document.querySelector('[data-analytics="ga"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
      script.setAttribute('data-analytics', 'ga');
      script.async = true;
      document.head.appendChild(script);
      
      script.onload = () => {
        (window as any).dataLayer = (window as any).dataLayer || [];
        function gtag(...args: any[]) {
          (window as any).dataLayer.push(args);
        }
        gtag('js', new Date());
        gtag('config', 'GA_MEASUREMENT_ID');
      };
    }
  }

  private loadMarketingScripts(): void {
    // Facebook Pixel
    if (!document.querySelector('[data-marketing="fb"]')) {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', 'YOUR_PIXEL_ID');
        fbq('track', 'PageView');
      `;
      script.setAttribute('data-marketing', 'fb');
      document.head.appendChild(script);
    }
  }

  private removeAnalyticsCookies(): void {
    // Remove Google Analytics cookies
    this.deleteCookie('_ga');
    this.deleteCookie('_ga_*');
    this.deleteCookie('_gid');
    
    // Remove GA scripts
    document.querySelectorAll('[data-analytics]').forEach(el => el.remove());
  }

  private removeMarketingCookies(): void {
    // Remove Facebook cookies
    this.deleteCookie('_fbp');
    this.deleteCookie('_fbc');
    
    // Remove marketing scripts
    document.querySelectorAll('[data-marketing]').forEach(el => el.remove());
  }

  private cleanupCookies(): void {
    this.removeAnalyticsCookies();
    this.removeMarketingCookies();
  }

  private deleteCookie(name: string): void {
    const domains = [window.location.hostname, `.${window.location.hostname}`];
    const paths = ['/', '/'];
    
    domains.forEach(domain => {
      paths.forEach(path => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain}; path=${path}`;
      });
    });
  }

  exportConsentData(): string {
    const consent = this.getConsent();
    if (!consent) return 'Keine Einwilligung vorhanden';
    
    return JSON.stringify({
      ...consent,
      timestamp: new Date(consent.timestamp).toISOString(),
      expiryDate: new Date(consent.expiryDate).toISOString()
    }, null, 2);
  }
}

export const cookieManager = new CookieManager();
